import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ModelPreviewPanel } from '@spazious/dollhouse-engine';
import { FCompositionType, VirtualObjectType } from '@spazious/dollhouse-graphql';
import { useGetVirtualObjectsAndFCompositionsSuspenseQuery } from '@spazious/dollhouse-graphql/apollo';
import { Translate, useTranslate } from '@spazious/dollhouse-i18n';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStore } from '@spazious/dollhouse-store';
import { styled } from '@spazious/dollhouse-theme';
import {
  Flex,
  LinkButton,
  Panel,
  PanelContent,
  PanelHeader,
  PanelProps,
  Typography,
} from '@spazious/dollhouse-uikit';

import { formatFCompositionSchemaName } from '../../utils/formatFCompositionSchemaName.js';
import { parseFCompositionIcon } from '../../utils/parseFCompositionIcon.js';
import { DraggableCard, DraggableCardOverlay } from '../DraggableCard/index.js';
import { ModelPreviewCard } from '../ModelPreviewCard/index.js';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 2fr));
  grid-gap: calc(2 * var(--spacing-padding));
  grid-auto-rows: 1fr;
`;

const Card = styled(Flex)`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  align-items: center;
  justify-content: center;
  border-radius: var(--shape-rounded-radius);
  border: solid 1px var(--spz-sys-color-surface-container-highest);
  background-color: var(--spz-sys-color-surface-variant);
  transition: border, background-color 0.2s ease-in-out;
  cursor: grab;
  &:hover {
    border: solid 1px var(--spz-sys-color-inverse-surface);
    background-color: var(--spz-sys-color-surface-container-lowest);
  }
`;

export interface FurnituresPanelProps extends PanelProps {}

export const FurnituresPanel: React.FC<FurnituresPanelProps> = () => {
  const ready = useStore((state) => state.status === 'ready');
  const translate = useTranslate();
  const spaceId = useGraph().graph.shallow.space;
  const previewType: 'image' | 'canvas' = 'image';
  const pageNumberRef = useRef(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { data, fetchMore } = useGetVirtualObjectsAndFCompositionsSuspenseQuery({
    skip: !ready || !spaceId,
    variables: {
      spaceId: `${spaceId}`!,
      objectType: VirtualObjectType.Furniture,
      fcompositionType: FCompositionType.Ensemble,
    },
  });

  const handleLoadMore = useCallback(async () => {
    if (!fetchMore) {
      setHasMore(false);
      return;
    }

    setIsLoading(true);
    try {
      pageNumberRef.current += 1;
      await fetchMore({
        variables: {
          pageNumber: pageNumberRef.current,
        },
      });
    } catch (error) {
      console.error('Error loading more furnitures:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchMore]);

  useEffect(() => {
    setHasMore(data?.virtualObjects.pagination?.hasNext ?? false);
  }, [data]);

  return (
    <Panel>
      <PanelHeader>
        <Flex column gap="var(--spacing-padding)">
          <Typography size="small" variant="headline">
            <Translate>Furniture</Translate>
          </Typography>
          <Flex alignItems="center">
            <Typography size="small" variant="title">
              <Translate>Drag and drop a furniture to the scene.</Translate>
            </Typography>
          </Flex>
        </Flex>
      </PanelHeader>
      <PanelContent style={{ position: 'relative' }}>
        <ModelPreviewPanel type={previewType}>
          <Grid>
            {(data?.virtualObjects.data ?? []).map(({ id, nid, model, thumbnail, name }, idx) => (
              <DraggableCard
                key={`${id}-furniture-${idx}`}
                node={{
                  type: FShallowNodeTypesEnum.Furniture,
                  id: nid,
                }}
              >
                <ModelPreviewCard
                  name={name}
                  thumbnail={thumbnail}
                  type={previewType}
                  url={model}
                />
              </DraggableCard>
            ))}
            {(data?.fcompositions ?? []).map(({ id, nid, name }) => {
              const Icon = parseFCompositionIcon(nid);
              return (
                <DraggableCard
                  key={`${id}-fcomposition`}
                  node={{
                    type: FShallowNodeTypesEnum.FComposition,
                    id: nid,
                  }}
                >
                  <Card column>
                    <Icon fontSize="50px" />
                    {/* i18n-extract-disable */}
                    {translate(formatFCompositionSchemaName(name))}
                  </Card>
                </DraggableCard>
              );
            })}
          </Grid>
          {hasMore && (
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{ padding: 'var(--spacing-padding)' }}
            >
              <LinkButton onClick={handleLoadMore}>
                {isLoading ? <Translate>Loading...</Translate> : <Translate>Load More</Translate>}
              </LinkButton>
            </Flex>
          )}
        </ModelPreviewPanel>
        <DraggableCardOverlay />
      </PanelContent>
    </Panel>
  );
};
